import { useEffect, useState, useCallback } from 'react'
import { Plus, Edit3, Trash2, Copy, Check, Image, Megaphone, Layers, Globe, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { screensAPI } from '@/lib/api'
import ColorPicker from '@/components/ColorPicker'

const KINDS = [
  { k: 'media',        label: 'Media',        icon: Image,     hint: 'Foto of video fullscreen' },
  { k: 'announcement', label: 'Aankondiging', icon: Megaphone, hint: 'Grote tekst met titel + subtitel' },
  { k: 'slideshow',    label: 'Slideshow',    icon: Layers,    hint: 'Reeks die vanzelf doorloopt' },
  { k: 'embed',        label: 'Embed',        icon: Globe,     hint: 'Willekeurige URL in iframe' },
]
const iconVanKind = (k) => (KINDS.find(x => x.k === k)?.icon ?? Globe)

const selectCls = 'w-full rounded-[4px] border border-border bg-background px-2 py-1.5 text-sm'

export default function CustomBoardsCard({ onWijziging, defaultOpen = false }) {
  const [boards, setBoards] = useState([])
  const [dlg, setDlg] = useState(null)
  const [gekopieerd, setGekopieerd] = useState(null)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState(defaultOpen)

  const laad = useCallback(() => {
    screensAPI.listBoards()
      .then(r => setBoards(r.data?.boards ?? []))
      .catch(e => setErr(e.response?.data?.error ?? e.message))
  }, [])
  useEffect(() => { laad() }, [laad])

  const opslaan = async () => {
    setErr(null)
    try {
      const body = { slug: dlg.slug, name: dlg.name, kind: dlg.kind, config: dlg.config || {} }
      if (dlg.id) await screensAPI.updateBoard(dlg.id, { name: dlg.name, kind: dlg.kind, config: dlg.config || {} })
      else        await screensAPI.createBoard(body)
      setDlg(null); laad(); onWijziging?.()
    } catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }

  const wis = async (id) => {
    if (!window.confirm('Deze inhoud verwijderen?')) return
    try { await screensAPI.deleteBoard(id); laad(); onWijziging?.() }
    catch (e) { setErr(e.response?.data?.error ?? e.message) }
  }

  const kopieerRoute = (slug) => {
    const route = `/scherm-invul/${slug}`
    navigator.clipboard?.writeText(route)
    setGekopieerd(slug); setTimeout(() => setGekopieerd(null), 1500)
  }

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <button type="button" onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 flex-1 text-left group">
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <CardTitle className="text-base flex items-center gap-2 group-hover:text-acid transition-colors">
            <Image className="h-4 w-4" /> Eigen inhouden
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
              · {boards.length}
            </span>
          </CardTitle>
        </button>
        {open && (
          <Button size="sm" onClick={() => setDlg({ slug: '', name: '', kind: 'announcement', config: {} })}>
            <Plus className="h-4 w-4 mr-1" /> Inhoud maken
          </Button>
        )}
      </CardHeader>
      {open && <CardContent className="space-y-2">
        {err && <p className="text-sm text-signal">{err}</p>}
        {boards.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nog geen eigen inhouden. Maak er een, kopieer de route en voeg die toe bij "Inhoud & APC-kolommen".
          </p>
        )}
        {boards.map(b => {
          const Icon = iconVanKind(b.kind)
          const route = `/scherm-invul/${b.slug}`
          return (
            <div key={b.id} className="flex items-center gap-2 rounded-[4px] border border-border bg-card px-3 py-2">
              <Icon className="h-4 w-4 text-acid shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{b.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground truncate">
                  {route} · <span className="uppercase tracking-widest">{b.kind}</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => kopieerRoute(b.slug)} title="Kopieer route">
                {gekopieerd === b.slug ? <Check className="h-4 w-4 text-live" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="sm" asChild title="Open dit scherm">
                <a href={route} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDlg({ id: b.id, slug: b.slug, name: b.name, kind: b.kind, config: b.config || {} })}>
                <Edit3 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => wis(b.id)}>
                <Trash2 className="h-4 w-4 text-signal" />
              </Button>
            </div>
          )
        })}
      </CardContent>}

      {dlg && (
        <Dialog open onOpenChange={(o) => !o && setDlg(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{dlg.id ? 'Inhoud bewerken' : 'Nieuwe inhoud'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={e => { e.preventDefault(); opslaan() }} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Naam *</Label>
                  <Input value={dlg.name} required
                         onChange={e => setDlg({ ...dlg, name: e.target.value })}
                         placeholder="Sponsorbanner" />
                </div>
                <div>
                  <Label>Slug (URL-deel) *</Label>
                  <Input value={dlg.slug} required disabled={!!dlg.id}
                         onChange={e => setDlg({ ...dlg, slug: e.target.value })}
                         placeholder="sponsor-1" />
                  <p className="text-xs text-muted-foreground mt-1">
                    Wordt <code className="font-mono">/scherm-invul/{dlg.slug || '…'}</code>
                  </p>
                </div>
              </div>

              <div>
                <Label>Soort</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 mt-1">
                  {KINDS.map(({ k, label, icon: Icon, hint }) => (
                    <button key={k} type="button" onClick={() => setDlg({ ...dlg, kind: k })}
                            className={`rounded-[4px] border p-3 text-left transition-colors ${
                              dlg.kind === k ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`h-4 w-4 ${dlg.kind === k ? 'text-acid' : ''}`} />
                        <span className="text-sm font-semibold">{label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-tight">{hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              {dlg.kind === 'media'        && <MediaEditor        c={dlg.config || {}} onC={c => setDlg({ ...dlg, config: c })} />}
              {dlg.kind === 'announcement' && <AnnouncementEditor c={dlg.config || {}} onC={c => setDlg({ ...dlg, config: c })} />}
              {dlg.kind === 'slideshow'    && <SlideshowEditor    c={dlg.config || {}} onC={c => setDlg({ ...dlg, config: c })} />}
              {dlg.kind === 'embed'        && <EmbedEditor        c={dlg.config || {}} onC={c => setDlg({ ...dlg, config: c })} />}

              {err && <p className="text-sm text-signal">{err}</p>}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDlg(null)}>Annuleer</Button>
                <Button type="submit">{dlg.id ? 'Opslaan' : 'Aanmaken'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  )
}

/* ── Editors per soort ────────────────────────────────────────── */

function MediaEditor({ c, onC }) {
  return (
    <div className="space-y-3 rounded-[4px] border border-border bg-card p-3">
      <div>
        <Label>URL naar foto of video *</Label>
        <Input value={c.url || ''} onChange={e => onC({ ...c, url: e.target.value })}
               placeholder="https://…/banner.jpg  of  /uploads/video.mp4" />
        <p className="text-xs text-muted-foreground mt-1">
          .mp4 / .webm / .mov worden als video geladen, andere extensies als foto.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Vulmodus</Label>
          <select value={c.mode || 'contain'}
                  onChange={e => onC({ ...c, mode: e.target.value })}
                  className={selectCls}>
            <option value="contain">Contain — hele beeld met kader</option>
            <option value="cover">Cover — vult scherm (crop kan)</option>
          </select>
        </div>
        <div>
          <Label>Achtergrondkleur</Label>
          <input type="color" value={c.bg || '#000000'}
                 onChange={e => onC({ ...c, bg: e.target.value })}
                 className="h-9 w-full rounded-[4px] border border-border cursor-pointer bg-transparent" />
        </div>
      </div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={c.video_muted !== false}
                 onChange={e => onC({ ...c, video_muted: e.target.checked })} />
          Video muted
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={c.video_loop !== false}
                 onChange={e => onC({ ...c, video_loop: e.target.checked })} />
          Video loopt
        </label>
      </div>
    </div>
  )
}

function AnnouncementEditor({ c, onC }) {
  return (
    <div className="space-y-3 rounded-[4px] border border-border bg-card p-3">
      <div>
        <Label>Eyebrow (klein bovenaan, optioneel)</Label>
        <Input value={c.eyebrow || ''} onChange={e => onC({ ...c, eyebrow: e.target.value })}
               placeholder="LAN Server 2026" />
      </div>
      <div>
        <Label>Titel *</Label>
        <Input value={c.title || ''} onChange={e => onC({ ...c, title: e.target.value })}
               placeholder="Pauze tot 20:00" />
      </div>
      <div>
        <Label>Subtitel</Label>
        <Input value={c.subtitle || ''} onChange={e => onC({ ...c, subtitle: e.target.value })}
               placeholder="Toernooi start direct na de pauze" />
      </div>
      <div>
        <Label>Grootte</Label>
        <select value={c.size || 'normal'} onChange={e => onC({ ...c, size: e.target.value })}
                className={selectCls}>
          <option value="normal">Normaal (groot)</option>
          <option value="huge">Extra groot (voor korte tekst)</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ColorPicker label="Tekstkleur" value={c.text_color}
                     defaultColor="#B4FF39" onChange={v => onC({ ...c, text_color: v })} />
        <ColorPicker label="Achtergrond" value={c.bg_color}
                     defaultColor="#0B0B0B" onChange={v => onC({ ...c, bg_color: v })} />
      </div>
    </div>
  )
}

function EmbedEditor({ c, onC }) {
  return (
    <div className="space-y-2 rounded-[4px] border border-border bg-card p-3">
      <div>
        <Label>URL *</Label>
        <Input value={c.url || ''} onChange={e => onC({ ...c, url: e.target.value })}
               placeholder="https://grafana.internal/…  of  /tournaments/leaderboard" />
        <p className="text-xs text-muted-foreground mt-1">
          Kan intern (start met /) of extern (https://…).
        </p>
      </div>
    </div>
  )
}

function SlideshowEditor({ c, onC }) {
  const items = Array.isArray(c.items) ? c.items : []
  const zetItems = (nieuw) => onC({ ...c, items: nieuw })
  const voegToe = (kind) => zetItems([...items, { kind, config: {} }])
  const wis = (i) => zetItems(items.filter((_, idx) => idx !== i))
  const zetItem = (i, item) => zetItems(items.map((v, idx) => idx === i ? item : v))
  const verschuif = (i, delta) => {
    const j = i + delta
    if (j < 0 || j >= items.length) return
    const kopie = [...items]
    ;[kopie[i], kopie[j]] = [kopie[j], kopie[i]]
    zetItems(kopie)
  }

  return (
    <div className="space-y-3 rounded-[4px] border border-border bg-card p-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <Label>Wisselinterval (seconden)</Label>
          <Input type="number" min="3" value={c.interval_seconds ?? 8}
                 onChange={e => onC({ ...c, interval_seconds: Number(e.target.value) })}
                 className="w-24" />
        </div>
        <div className="flex-1" />
        <div className="flex gap-1">
          <Button type="button" size="sm" variant="outline" onClick={() => voegToe('media')}>+ media</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => voegToe('announcement')}>+ tekst</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => voegToe('embed')}>+ embed</Button>
        </div>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-muted-foreground">Nog geen items. Voeg er hierboven een toe.</p>
      )}

      {items.map((item, i) => (
        <div key={i} className="rounded-[4px] border border-border p-2 bg-background/40">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              #{i + 1} · {item.kind}
            </span>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant="ghost" onClick={() => verschuif(i, -1)} disabled={i === 0}>↑</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => verschuif(i, +1)} disabled={i === items.length - 1}>↓</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => wis(i)}>
                <Trash2 className="h-3.5 w-3.5 text-signal" />
              </Button>
            </div>
          </div>
          {item.kind === 'media'        && <MediaEditor        c={item.config} onC={cfg => zetItem(i, { ...item, config: cfg })} />}
          {item.kind === 'announcement' && <AnnouncementEditor c={item.config} onC={cfg => zetItem(i, { ...item, config: cfg })} />}
          {item.kind === 'embed'        && <EmbedEditor        c={item.config} onC={cfg => zetItem(i, { ...item, config: cfg })} />}
        </div>
      ))}
    </div>
  )
}
